import React from 'react';
import { Box, Button } from '@mui/material';

type WhiteNoteName = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

type KeyboardRowConfig = {
  label: string;
  totalKeys: number;
  width: string;
  maxWidth: string;
  whiteKeys: number;
  startNote: WhiteNoteName;
  highlightIndex: number;
  keyboardHeight: number;
  labelFontSize: string;
};

const whiteNoteCycle: WhiteNoteName[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const hasBlackKeyAfter = new Set<WhiteNoteName>(['C', 'D', 'F', 'G', 'A']);

const keyboardRows: KeyboardRowConfig[] = [
  {
    label: 'Full piano keyboard',
    totalKeys: 88,
    width: '100%',
    maxWidth: '980px',
    whiteKeys: 52,
    startNote: 'A',
    highlightIndex: 23,
    keyboardHeight: 126,
    labelFontSize: 'clamp(7px, 0.72vw, 12px)',
  },
  {
    label: 'Medium keyboard',
    totalKeys: 49,
    width: '67%',
    maxWidth: '590px',
    whiteKeys: 29,
    startNote: 'C',
    highlightIndex: 14,
    keyboardHeight: 110,
    labelFontSize: 'clamp(11px, 1vw, 17px)',
  },
  {
    label: 'Compact keyboard',
    totalKeys: 25,
    width: '41%',
    maxWidth: '372px',
    whiteKeys: 15,
    startNote: 'C',
    highlightIndex: 7,
    keyboardHeight: 100,
    labelFontSize: 'clamp(12px, 1.05vw, 18px)',
  },
];

const buildWhiteKeyLabels = (startNote: WhiteNoteName, whiteKeys: number) => {
  const startIndex = whiteNoteCycle.indexOf(startNote);
  return Array.from({ length: whiteKeys }, (_, index) => {
    return whiteNoteCycle[(startIndex + index) % whiteNoteCycle.length];
  });
};

const KeyboardReferenceRow: React.FC<KeyboardRowConfig> = ({
  label,
  totalKeys,
  width,
  maxWidth,
  whiteKeys,
  startNote,
  highlightIndex,
  keyboardHeight,
  labelFontSize,
}) => {
  const labels = buildWhiteKeyLabels(startNote, whiteKeys);
  const keybedInset = {
    xs: '34px 16px 10px',
    md: '38px 18px 12px',
  };

  return (
    <Box
      data-middle-c-keyboard-reference
      data-key-count={totalKeys}
      aria-label={`${label}, ${totalKeys} keys`}
      sx={{
        width,
        maxWidth,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: `${keyboardHeight}px`,
          padding: { xs: '11px 12px 9px', md: '13px 16px 11px' },
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '9px',
          background: 'linear-gradient(180deg, #30343d 0%, #111319 11%, #050609 52%, #151820 100%)',
          boxShadow:
            '0 16px 30px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.86)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '5px',
            left: '16px',
            right: '16px',
            height: '20%',
            borderRadius: '7px 7px 2px 2px',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.015) 100%)',
            opacity: 0.72,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '18px',
            background:
              'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(255,255,255,0.035) 42%, rgba(0,0,0,0.38) 100%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: keybedInset,
            borderTop: '3px solid #5d2436',
            boxShadow: '0 -1px 0 rgba(255,255,255,0.12)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${whiteKeys}, minmax(0, 1fr))`,
              alignItems: 'end',
            }}
          >
            {labels.map((label, index) => (
              <Box
                key={`white-${label}-${index}`}
                data-piano-white-key
                aria-label={index === highlightIndex ? 'Middle C (C4)' : undefined}
                sx={{
                  position: 'relative',
                  height: '100%',
                  borderLeft: index === 0 ? '1px solid rgba(54, 57, 64, 0.56)' : 'none',
                  borderRight: '1px solid rgba(65, 68, 74, 0.4)',
                  borderBottom: '1px solid rgba(66, 68, 74, 0.48)',
                  borderRadius: '0 0 2px 2px',
                  background:
                    'linear-gradient(90deg, #e9e9e8 0%, #ffffff 18%, #fbfaf8 74%, #d7d7d6 100%)',
                  boxShadow:
                    'inset 0 -11px 14px rgba(190, 190, 188, 0.46), inset 0 1px 0 rgba(255,255,255,0.9), 0 2px 2px rgba(0,0,0,0.2)',
                }}
              >
                {index === highlightIndex && (
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(174, 190, 255, 0.34) 0%, rgba(117, 143, 255, 0.82) 100%)',
                        boxShadow:
                          'inset 0 0 0 1px rgba(85, 110, 225, 0.74), 0 0 13px rgba(112, 139, 255, 0.5)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: '0 auto 0 0',
                        width: '100%',
                        background: 'rgba(150, 166, 255, 0.32)',
                        mixBlendMode: 'multiply',
                      }}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {labels.map((label, index) => {
              if (!hasBlackKeyAfter.has(label) || index >= labels.length - 1) return null;

              return (
                <Box
                  key={`black-${label}-${index}`}
                  data-piano-black-key
                  sx={{
                    position: 'absolute',
                    top: '-1px',
                    left: `${((index + 1) / whiteKeys) * 100}%`,
                    transform: 'translateX(-50%)',
                    width: `calc((100% / ${whiteKeys}) * 0.62)`,
                    height: '60%',
                    border: '1px solid #020204',
                    borderRadius: '0 0 3px 3px',
                    background:
                      'linear-gradient(90deg, #020205 0%, #262932 22%, #08090d 68%, #1d2028 100%)',
                    boxShadow:
                      '0 7px 8px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -6px 7px rgba(255,255,255,0.06)',
                    zIndex: 3,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${whiteKeys}, minmax(0, 1fr))`,
          color: '#0E1117',
          fontWeight: 700,
          letterSpacing: '0.02em',
          lineHeight: 1,
          fontSize: labelFontSize,
          userSelect: 'none',
        }}
      >
        {labels.map((label, index) => (
          <Box
            key={`label-${label}-${index}`}
            sx={{
              textAlign: 'center',
              color: index === highlightIndex ? '#7B91FF' : '#0E1117',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type MiddleCReferenceProps = {
  onContinue?: () => void;
};

const MiddleCReference: React.FC<MiddleCReferenceProps> = ({ onContinue }) => {
  const [selectedKeyboardIndex, setSelectedKeyboardIndex] = React.useState(0);
  const selectedKeyboard = keyboardRows[selectedKeyboardIndex];

  return (
    <Box
      data-acoustic-setup-step="middle-c"
      sx={{
        borderRadius: '18px',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 100%)',
        border: '1px solid rgba(121, 216, 255, 0.18)',
        padding: { xs: '14px', md: '14px 16px' },
      }}
    >
      <Box
        sx={{
          color: '#F5F9FF',
          fontSize: { xs: '20px', md: '22px' },
          fontWeight: 700,
          letterSpacing: 0,
          textAlign: 'center',
          marginBottom: '4px',
        }}
      >
        Find middle C
      </Box>

      <Box
        sx={{
          color: 'rgba(219, 228, 247, 0.76)',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '10px',
        }}
      >
        Select the keyboard closest to yours.
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '10px',
        }}
      >
        {keyboardRows.map((row, index) => {
          const selected = selectedKeyboardIndex === index;
          return (
            <Button
              key={row.label}
              data-middle-c-keyboard-size={row.label.toLowerCase().replace(' keyboard', '')}
              aria-label={row.label}
              aria-pressed={selected}
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => setSelectedKeyboardIndex(index)}
              sx={{
                minHeight: '42px',
                paddingX: '15px',
                borderRadius: '10px',
                borderColor: selected ? '#6F87F1' : '#667086',
                backgroundColor: selected ? '#2A3866' : 'transparent',
                color: '#F2F6FF',
                fontSize: '13px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#8298FF',
                  backgroundColor: selected ? '#2A3866' : 'rgba(111, 135, 241, 0.08)',
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', whiteSpace: 'nowrap' }}
              >
                {row.label}
                <Box
                  component="span"
                  sx={{
                    color: selected ? '#C7D2FF' : 'rgba(219, 228, 247, 0.64)',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  {row.totalKeys} keys
                </Box>
              </Box>
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          borderRadius: '16px',
          background: 'linear-gradient(180deg, #e6e6e6 0%, #d9d9d9 100%)',
          minHeight: { xs: '166px', md: '176px' },
          padding: { xs: '12px 10px', md: '14px 14px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <KeyboardReferenceRow
          key={`${selectedKeyboard.startNote}-${selectedKeyboard.whiteKeys}`}
          {...selectedKeyboard}
        />
      </Box>

      {onContinue && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <Button
            data-acoustic-setup-continue
            variant="contained"
            onClick={onContinue}
            sx={{
              minHeight: '42px',
              paddingX: '22px',
              borderRadius: '10px',
              backgroundColor: '#586FCB',
              color: '#FFFFFF',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#4C61B4' },
            }}
          >
            Continue
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MiddleCReference;
